import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";

export default function Header() {
  const { t } = useLanguage();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const links = [
    { to: "/", label: t("nav_home") },
    { to: "/butikk", label: t("nav_shop") },
    { to: "/pakker", label: "Pakker" },
    { to: "/om-oss", label: t("nav_about") },
  ];

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header className="backdrop-blur-xl bg-[#F9F8F6]/80 border-b border-[#E5E2DC] sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
            <span className="font-heading text-2xl tracking-tight font-medium text-[#2C302E]">
              småstund
            </span>
            <span className="hidden md:inline-block w-1.5 h-1.5 rounded-full bg-[#D97B66]"></span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-10">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.replace("/", "") || "home"}`}
                className={`text-sm tracking-wide transition-colors ${
                  isActive(l.to)
                    ? "text-[#2C302E] font-medium"
                    : "text-[#6B726C] hover:text-[#2C302E]"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-6">
            <Link
              to="/handlekurv"
              data-testid="cart-link"
              className="relative p-2 hover:bg-[#E5E2DC]/40 rounded-full transition-colors"
            >
              <ShoppingBag className="w-5 h-5 text-[#2C302E]" strokeWidth={1.5} />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-0.5 -right-0.5 bg-[#D97B66] text-white text-[10px] font-medium rounded-full w-5 h-5 flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              className="md:hidden p-2"
              onClick={() => setOpen(!open)}
            >
              {open ? (
                <X className="w-5 h-5" strokeWidth={1.5} />
              ) : (
                <Menu className="w-5 h-5" strokeWidth={1.5} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {open && (
          <div className="md:hidden pb-6 border-t border-[#E5E2DC] -mx-6 px-6 pt-4">
            <nav className="flex flex-col gap-4">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                  className="text-base text-[#2C302E] py-2"
                >
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
